import { useState, useRef } from 'react';
import { Camera, X, Check, AlertTriangle, Trash2 } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { reportError } from '../../../lib/errors';

interface WalkaroundCaptureProps {
    jobId: string;
    isOpen: boolean;
    onClose: () => void;
    onSave?: () => void;
}

// Vehicle zones
const ZONES = [
    { id: 'front', label: 'Front', icon: '🚗' },
    { id: 'rear', label: 'Rear', icon: '🔙' },
    { id: 'left', label: 'Left Side', icon: '⬅️' },
    { id: 'right', label: 'Right Side', icon: '➡️' },
    { id: 'interior', label: 'Interior', icon: '🪑' },
    { id: 'roof', label: 'Roof', icon: '☁️' }
] as const;

// Damage presets
const DAMAGE_PRESETS = [
    { id: 'scratch', label: 'Scratch', color: 'bg-gold-fill/20 text-gold border-gold/30' },
    { id: 'dent', label: 'Dent', color: 'bg-warning/20 text-warning border-warning/30' },
    { id: 'chip', label: 'Stone Chip', color: 'bg-info/20 text-info border-info/30' },
    { id: 'crack', label: 'Crack', color: 'bg-danger/20 text-danger border-danger/30' },
    { id: 'rust', label: 'Rust', color: 'bg-gold-fill/20 text-gold border-gold/30' },
    { id: 'stain', label: 'Stain', color: 'bg-chart-4/20 text-chart-4 border-chart-4/30' },
    { id: 'worn', label: 'Worn', color: 'bg-tint/10 text-fg-3 border-line-glow/30' },
    { id: 'missing', label: 'Missing Part', color: 'bg-chart-5/20 text-chart-5 border-chart-5/30' }
];

type ZoneId = typeof ZONES[number]['id'];

interface ZoneData {
    notes: string;
    photos: string[];
}

export default function WalkaroundCapture({ jobId, isOpen, onClose, onSave }: WalkaroundCaptureProps) {
    const { activeOrgId } = useVertical();
    const [activeZone, setActiveZone] = useState<ZoneId>('front');
    const [zoneData, setZoneData] = useState<Record<ZoneId, ZoneData>>({
        front: { notes: '', photos: [] },
        rear: { notes: '', photos: [] },
        left: { notes: '', photos: [] },
        right: { notes: '', photos: [] },
        interior: { notes: '', photos: [] },
        roof: { notes: '', photos: [] }
    });
    const [damageTags, setDamageTags] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Update zone notes
    const updateZoneNotes = (zoneId: ZoneId, notes: string) => {
        setZoneData(prev => ({
            ...prev,
            [zoneId]: { ...prev[zoneId], notes }
        }));
    };

    // Toggle damage tag
    const toggleDamageTag = (damageId: string) => {
        const tagId = `${damageId}_${activeZone}`;
        setDamageTags(prev =>
            prev.includes(tagId)
                ? prev.filter(t => t !== tagId)
                : [...prev, tagId]
        );
    };

    // Check if a damage tag is active for current zone
    const isDamageActive = (damageId: string) => {
        return damageTags.includes(`${damageId}_${activeZone}`);
    };

    // Handle photo upload
    const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0 || !activeOrgId) return;

        setUploading(true);

        try {
            const uploadedUrls: string[] = [];
            let failed = 0;

            for (const file of Array.from(files)) {
                const ext = file.name.split('.').pop();
                const fileName = `${jobId}/${activeZone}/${Date.now()}.${ext}`;

                const { error: uploadError } = await supabase.storage
                    .from('walkaround-photos')
                    .upload(fileName, file, { upsert: true });

                if (uploadError) {
                    // This used to `continue` after a console.error, so when
                    // every upload failed the modal simply stopped spinning and
                    // said nothing — the user had no way to tell whether their
                    // photo was attached to the job or had vanished.
                    reportError(uploadError, 'walkaround.photo-upload', { jobId, zone: activeZone });
                    failed += 1;
                    continue;
                }

                const { data: urlData } = supabase.storage
                    .from('walkaround-photos')
                    .getPublicUrl(fileName);

                if (urlData?.publicUrl) {
                    uploadedUrls.push(urlData.publicUrl);
                } else {
                    failed += 1;
                }
            }

            if (uploadedUrls.length > 0) {
                setZoneData(prev => ({
                    ...prev,
                    [activeZone]: {
                        ...prev[activeZone],
                        photos: [...prev[activeZone].photos, ...uploadedUrls]
                    }
                }));
                toast.success(
                    uploadedUrls.length === 1
                        ? 'Photo added'
                        : `${uploadedUrls.length} photos added`
                );
            }
            if (failed > 0) {
                toast.error(
                    uploadedUrls.length === 0
                        ? failed === 1 ? 'That photo did not upload. Try again.'
                                       : 'None of those photos uploaded. Try again.'
                        : `${failed} of those photos did not upload.`
                );
            }
        } catch (err) {
            reportError(err, 'walkaround.photo-upload', { jobId });
            toast.error('Photo upload failed. Try again.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    // Remove photo
    const removePhoto = (zoneId: ZoneId, photoUrl: string) => {
        setZoneData(prev => ({
            ...prev,
            [zoneId]: {
                ...prev[zoneId],
                photos: prev[zoneId].photos.filter(p => p !== photoUrl)
            }
        }));
    };

    // Save walkaround
    const handleSave = async () => {
        if (!activeOrgId) return;

        setSaving(true);

        try {
            // Format notes and photos as JSONB
            const notes: Record<string, string> = {};
            const photos: Record<string, string[]> = {};

            Object.entries(zoneData).forEach(([zoneId, data]) => {
                notes[zoneId] = data.notes;
                photos[zoneId] = data.photos;
            });

            const { error } = await supabase
                .from('walkarounds')
                .insert({
                    job_id: jobId,
                    org_id: activeOrgId,
                    notes,
                    photos,
                    damage_tags: damageTags
                });

            if (error) throw error;

            toast.success('Walkaround saved to this job');
            onSave?.();
            onClose();
        } catch (err) {
            reportError(err, 'walkaround.save', { jobId });
            toast.error('Failed to save walkaround');
        } finally {
            setSaving(false);
        }
    };

    // Count damages for a zone
    const getZoneDamageCount = (zoneId: ZoneId): number => {
        return damageTags.filter(t => t.endsWith(`_${zoneId}`)).length;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-scrim/90 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-4xl bg-panel border border-line rounded-xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col"
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-tint/10 bg-raised">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-gold-fill/10 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-fg">Vehicle Walkaround</h2>
                            <p className="text-xs text-fg-4">Document existing damage before repairs</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-tint/10 text-fg-3 hover:text-fg"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
                    {/* Zone Selector (Left) */}
                    <div className="w-full md:w-48 border-b md:border-b-0 md:border-r border-tint/10 p-3 flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible">
                        {ZONES.map(zone => (
                            <button
                                key={zone.id}
                                onClick={() => setActiveZone(zone.id)}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all flex-shrink-0 ${activeZone === zone.id
                                    ? 'fd-btn-primary'
                                    : 'bg-tint/5 text-fg-3 hover:bg-tint/10 hover:text-fg'
                                    }`}
                            >
                                <span className="text-lg">{zone.icon}</span>
                                <span className="hidden md:inline">{zone.label}</span>
                                {/* Badge for damages */}
                                {getZoneDamageCount(zone.id) > 0 && (
                                    <span className="ml-auto text-[10px] bg-danger-fill text-on-fill px-1.5 py-0.5 rounded-full font-bold">
                                        {getZoneDamageCount(zone.id)}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Zone Content (Right) */}
                    <div className="flex-1 p-4 overflow-y-auto space-y-6">
                        {/* Damage Tags */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-2">
                                Pre-existing Damage ({ZONES.find(z => z.id === activeZone)?.label})
                            </label>
                            <div className="flex flex-wrap gap-2">
                                {DAMAGE_PRESETS.map(damage => (
                                    <button
                                        key={damage.id}
                                        onClick={() => toggleDamageTag(damage.id)}
                                        className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${isDamageActive(damage.id)
                                            ? damage.color + ' ring-2 ring-tint/20'
                                            : 'bg-tint/5 text-fg-4 border-tint/10 hover:border-tint/20'
                                            }`}
                                    >
                                        {isDamageActive(damage.id) && <Check className="w-3 h-3 inline mr-1" />}
                                        {damage.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-2">
                                Notes
                            </label>
                            <textarea
                                value={zoneData[activeZone].notes}
                                onChange={e => updateZoneNotes(activeZone, e.target.value)}
                                rows={3}
                                className="w-full bg-tint/5 border border-tint/10 rounded-lg px-4 py-3 text-fg text-sm focus:ring-2 focus:ring-accent outline-none resize-none"
                                placeholder="Describe any existing damage in this area..."
                            />
                        </div>

                        {/* Photos */}
                        <div>
                            <label className="block text-xs font-bold text-fg-4 uppercase mb-2">
                                Photos
                            </label>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-3">
                                {/* Existing photos */}
                                {zoneData[activeZone].photos.map((photo, idx) => (
                                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden group">
                                        <img
                                            src={photo}
                                            alt={`${activeZone} photo ${idx + 1}`}
                                            className="w-full h-full object-cover"
                                        />
                                        <button
                                            onClick={() => removePhoto(activeZone, photo)}
                                            className="absolute top-1 right-1 p-1.5 bg-danger-fill rounded-full fd-hover-reveal"
                                        >
                                            <Trash2 className="w-3 h-3 text-fg" />
                                        </button>
                                    </div>
                                ))}

                                {/* Add photo button */}
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={uploading}
                                    className="aspect-square rounded-lg border-2 border-dashed border-tint/20 flex flex-col items-center justify-center gap-1 text-fg-4 hover:border-accent hover:text-accent transition-colors"
                                >
                                    {uploading ? (
                                        <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            <Camera className="w-5 h-5" />
                                            <span className="text-[10px] font-bold uppercase">Add</span>
                                        </>
                                    )}
                                </button>
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handlePhotoUpload}
                                className="hidden"
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-tint/10 flex items-center justify-between bg-raised">
                    <div className="text-xs text-fg-4">
                        {damageTags.length} damage{damageTags.length !== 1 ? 's' : ''} recorded
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-fg-3 hover:text-fg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="px-5 py-2 fd-btn-primary font-bold text-sm rounded-lg transition-all flex items-center gap-2 disabled:opacity-50"
                        >
                            {saving ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    Save Walkaround
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </motion.div>
        </div>
    );
}
